import axios from 'axios';

import { ToastService } from '@iamsoftware/react-hooks';

import { signingURL } from 'src/config';
import { AgreementService } from 'src/service/AgreementService';

export class DocumentSignService {

    static async sign(data: any) {
        try {
            const resp: any = await AgreementService.pullSigningData({
                agreementId: data.agreementId,
                partyId: data.partyId,
                roleTypeId: data.roleTypeId,
                referenceNumber: data.referenceNumber,
                signingSecretCode: data.signingSecretCode
            });
            
            if (!resp) {
                throw new Error('Không nhận được dữ liệu từ máy chủ');
            }

            if (resp.errors || resp.errorMessage) {
                const errorMessage = resp.errors || resp.errorMessage;
                ToastService.error(errorMessage);
                return { ...resp, success: false, errorMessage };
            }

            const documentList = (resp as any)?.documentList;
            const signingBlocks = resp?.signatureLocationList || (documentList?.[0]?.signatureLocationList);
            const fileName = resp?.fileName || (documentList?.[0]?.fileName);
            const pdfBase64 = resp?.base64FileData || (documentList?.[0]?.base64FileData) || data.pdfBase64;
            const pulledAgreementId = (resp as any)?.agreementId || data.agreementId;
            const pulledPartyId = (resp as any)?.partyId || data.partyId;
            const pulledRoleTypeId = (resp as any)?.roleTypeId || data.roleTypeId;
            const taxCode = (resp as any)?.partyIdValue || null;
            const isCheck = (resp as any)?.isCheck || 'N';

            const agentPayload: any = {
                ...data,
                taxCode,
                isCheck,
                DisplayConfig: resp.displayConfig
            };
            if (!documentList || documentList.length === 0) {
                agentPayload.pdfBase64 = pdfBase64;
                agentPayload.fileName = fileName;
                agentPayload.signingBlocks = (signingBlocks || []).map((block: any) => ({
                    X: block.llx,
                    Y: block.lly,
                    Width: block.widthPx,
                    Height: block.heightPx,
                    Page: block.pageNumber,
                    PageNumber: block.pageNumber,
                    PageWidth: block.pageWidth,
                    PageHeight: block.pageHeight,
                    LocationSeqId: block.locationSeqId,
                    FontSize: block.fontSize,
                    name: block.name,
                    signatureType: block.signatureTypeEnumId || block.signatureType || 'AgtDigitalVerified',
                    visualData: block.visualData || (block.locationSeqId && data.visualDataMap ? data.visualDataMap[block.locationSeqId] : null),
                    displayConfig: block.displayConfig || resp.displayConfig
                }));
            }

            if (documentList && documentList.length > 0) {
                agentPayload.documentList = documentList.map((doc: any) => ({
                    agreementId: doc.agreementId,
                    agreementContentId: doc.agreementContentId,
                    fileName: doc.fileName,
                    base64FileData: doc.base64FileData,
                    signingBlocks: (doc.signatureLocationList || []).map((block: any) => ({
                        X: block.llx,
                        Y: block.lly,
                        Width: block.widthPx,
                        Height: block.heightPx,
                        Page: block.pageNumber,
                        PageNumber: block.pageNumber,
                        PageWidth: block.pageWidth,
                        PageHeight: block.pageHeight,
                        LocationSeqId: block.locationSeqId,
                        FontSize: block.fontSize,
                        name: block.name,
                        signatureType: block.signatureTypeEnumId || block.signatureType || 'AgtDigitalVerified',
                        visualData: block.visualData || (block.locationSeqId && data.visualDataMap ? data.visualDataMap[block.locationSeqId] : null),
                        displayConfig: block.displayConfig
                    }))
                }));
            }

            return axios.post(signingURL, agentPayload)
                .then(res => res.data)
                .then(async responseData => {
                    if (responseData?.success) {
                        ToastService.success();
                    } else if (responseData?.errorMessage || responseData?.message) {
                        if (responseData.agreementId && responseData.partyId && responseData.roleTypeId) {
                            try {
                                await AgreementService.updateSigningError({
                                    agreementId: responseData.agreementId,
                                    partyId: responseData.partyId,
                                    roleTypeId: responseData.roleTypeId,
                                    referenceNumber: responseData.referenceNumber || null,
                                    signingSecretCode: responseData.signingSecretCode || ''
                                });
                            } catch (apiError) {
                            }
                        }
                        ToastService.error(responseData?.errorMessage || 'Ký số thất bại!');
                    }

                    return {
                        ...responseData,
                        signatureLocationList: signingBlocks,
                        agreementId: pulledAgreementId || responseData?.agreementId,
                        partyId: pulledPartyId || responseData?.partyId,
                        roleTypeId: pulledRoleTypeId || responseData?.roleTypeId,
                        documentList: documentList
                    };
                }).catch(async err => {
                    const errorParams = {
                        agreementId: data.agreementId || pulledAgreementId,
                        partyId: data.partyId || pulledPartyId,
                        roleTypeId: data.roleTypeId || pulledRoleTypeId || 'PartyA',
                        referenceNumber: data.referenceNumber || null,
                        signingSecretCode: data.signingSecretCode || ''
                    };
                    try {
                        await AgreementService.updateSigningError(errorParams);
                    } catch (apiError) {}
                    ToastService.error('Ký số thất bại!');
                    throw err;
                });

        } catch (e: any) {
            const errorMessage = e?.response?.data?.errors || e?.response?.data?.errorMessage || e?.message || 'Không thể tải dữ liệu ký';
            ToastService.error(errorMessage);
            return { success: false, error: e, errorMessage };
        }
    }

    static async bulkSign(items: any[]) {
        try {
            const agreementIds = Array.from(new Set(items.map(item => item.agreementId)));
            
            // Bước 1: Pull dữ liệu lô hàng loạt từ server
            const batchInfo: any = await AgreementService.pullBatchData(agreementIds);
            if (batchInfo) {
                const partyTaxCode = batchInfo.partyIdValue || null;
                const documentList = batchInfo.documentList; 
                const isCheck = batchInfo.isCheck || 'N';

                // Bước 2: Gửi danh sách dữ liệu sang Agent
                const responseData = await axios.post(signingURL, {
                    agreementIds,
                    taxCode: partyTaxCode,
                    isCheck,
                    DisplayConfig: batchInfo.displayConfig,
                    documentList: documentList.map((doc: any) => ({
                        ...doc,
                        signingBlocks: (doc.signatureLocationList || []).map((block: any) => ({
                            ...block,
                            X: block.llx,
                            Y: block.lly,
                            Width: block.widthPx,
                            Height: block.heightPx,
                            Page: block.pageNumber,
                            PageNumber: block.pageNumber,
                            PageWidth: block.pageWidth,
                            PageHeight: block.pageHeight,
                            LocationSeqId: block.locationSeqId,
                            FontSize: block.fontSize,
                            name: block.name,
                            signatureType: block.signatureTypeEnumId || block.signatureType || 'AgtDigitalVerified',
                            visualData: block.visualData || null
                        }))
                    }))
                }).then(res => res.data);
        
                if (responseData?.success) {
                    // Bước 3: Đẩy file đã ký về server lưu trữ
                    if (responseData.data?.signedAgreements) {
                        try {
                            await AgreementService.pushBatchData(responseData.data.signedAgreements);
                            ToastService.success('Ký hàng loạt và lưu file thành công!');
                        } catch (pushErr: any) {
                            const pushErrMsg = pushErr?.response?.data?.errors || 'Ký Agent thành công nhưng lỗi khi lưu file về server!';
                            ToastService.error(pushErrMsg);
                            return { success: false, error: pushErr, errorMessage: pushErrMsg };
                        }
                    } else {
                        ToastService.success('Ký hàng loạt thành công!');
                    }
                } else if (responseData?.errorMessage || responseData?.message) {
                    ToastService.error(responseData?.errorMessage || responseData?.message || 'Ký số hàng loạt thất bại!');
                }
                
                return responseData;
            }
        } catch (err: any) {
            const agreementIds = Array.from(new Set(items.map(item => item.agreementId)));
            // Xử lý lỗi khi gọi Agent (mất kết nối, người dùng huỷ...)
            if (agreementIds && agreementIds.length > 0) {
                try {
                    await AgreementService.updateBatchSigningErr(agreementIds);
                } catch (resetErr) {
                    console.error('>>> reset status error:', resetErr);
                }
            }
            const errorMessage = err?.response?.data?.errors || err?.message || 'Không thể kết nối tới Agent USB Token!';
            ToastService.error(errorMessage);
            return { success: false, error: err, errorMessage };
        }
    }
}
